import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import { useQuery } from 'react-query';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
// @mui
import { Container, Stack, Box, Typography, Grid } from '@mui/material';
// components
import Logo from 'src/components/Logo';
import GenericImpactMetric from 'src/components/impact/GenericImpactMetric';
import SdgCountTile from 'src/components/SdgCountTile';
import Image from 'src/components/Image';
import Title from 'src/components/reporting/Title';
import LoadingScreen from 'src/components/LoadingScreen';
// sections
import Instruction from 'src/sections/reporting/printable/Instruction';
import TotalImpactSection from 'src/sections/reporting/printable/TotalImpactSection';
import CumulativeImpactSection from 'src/sections/reporting/printable/CumulativeImpactSection';
import ImpactGraphs from 'src/sections/reporting/ImpactGraphs';
import Projects from 'src/sections/reporting/printable/Projects';
import SimpleTable from 'src/components/table/SimpleTable';
// hooks
import useMerchant from 'src/hooks/useMerchant';
import useReportData from 'src/hooks/useReportData';
import useReportingCalculations from 'src/hooks/useReportingCalculations';
// api
import { getAviationStatsApi, getImpactStatsApi } from 'src/api/reportingApi';
import { getProjectsApi } from 'src/api/projectsApi';
//
import { getListOfUniqSDGs } from 'src/utils/sustainableDevelopmentGoals';
import { formatNumber } from 'src/utils/numberFormatter';
import { ImpactRowReporting, ProgrammeType, ProjectImpact, ReportingTooltip } from 'src/@types/reporting';
import { Project } from 'src/@types/basket';
import { TransactionOwnershipType } from 'src/@types/transactions';
import './reporting.css';
import Summary from 'src/sections/reporting/engagement/Summary';
import { generateProgrammeLink } from 'src/utils/programmes';
import CabinClassBreakdown from 'src/sections/reporting/engagement/CabinClassBreakdown';
import DistanceBreakdown from 'src/sections/reporting/engagement/DistanceBreakdown';
import { readableStringFormatter } from 'src/utils/stringFormatter';

// ----------------------------------------------------------------------

interface TableRowProps {
  key: string;
  co2Kgs: number;
  numberOfContributions: number;
  endOfPeriod?: number;
  contributions?: number;
}

const FONT_FAMILY = 'MiradorLight, Newsreader, Georgia, serif';
const TOTAL_MERCHANT_IMPACT_QUERY_KEY = 'totalMerchantImpact';
const AVIATION_STATS_QUERY_KEY = 'aviationStats';
const MERCHANT_TOTAL_IMPACT_STATS_QUERY_KEY = 'merchantTotalImpactStats';

interface Props {
  ownershipTypes?: TransactionOwnershipType[];
}

const MerchantReport: React.FC<Props> = ({ ownershipTypes }) => {
  const { code, type, dateFrom, dateTo } = useParams();  
  const isAviation = type?.toUpperCase() === ProgrammeType.FLIGHT;
  
  const { merchant } = useMerchant();
  const { enqueueSnackbar } = useSnackbar();
  const { getSummaryDataByProgramme } = useReportingCalculations();

  const dynamicTotalImpactParams = useCallback((typeF?: string) => {
    const programmeCodeParam = code ? code : undefined;
    const dateFromParam = typeF === 'dateRanged' ? dateFrom : undefined;
    const dateToParam = typeF === 'dateRanged' ? dateTo : undefined;
    const ownershipTypeParam = ownershipTypes ? ownershipTypes : undefined;

    const params = {
      merchantCode: merchant?.code,
      programmeCode: programmeCodeParam,
      dateFrom: dateFromParam,
      dateTo: dateToParam,
      ownershipType: ownershipTypeParam,
    }
    return {
      key: MERCHANT_TOTAL_IMPACT_STATS_QUERY_KEY,
      fetchFunction: getImpactStatsApi,
      params,
    };
  }, [code, dateFrom, dateTo, merchant?.code, ownershipTypes]);

  const { data: totalStats, isLoading: isLoadingTotalStats } =  useReportData(dynamicTotalImpactParams());
  const { data: impactStats, isLoading: isLoadingImpactStats } =  useReportData(dynamicTotalImpactParams('dateRanged'));

  const projectIds = useMemo(
    () => impactStats?.data?.projectImpacts?.map((el: ProjectImpact) => el.projectId),
    [impactStats?.data?.projectImpacts]
  );

  const statuses = ['PUBLISHED', 'INACTIVE', 'PRIVATE', 'ACTIVE'];

  const { data: projectsRQ, isLoading: isLoadingProjects } = useQuery(
    [TOTAL_MERCHANT_IMPACT_QUERY_KEY, merchant?.code, projectIds],
    () => getProjectsApi(null, merchant!.code, undefined, { projectIds, statuses }),
    {
      enabled: !!merchant?.code && !!projectIds?.length,
      onError: (error) => {
        enqueueSnackbar(`Failed to fetch projects!`, { variant: 'error' });
      },
    }
  );

  const dynamicAviationParams = useMemo(() => {
    const programmeCodeParam = code ? code : undefined;

    const params = {
      merchantCode: merchant?.code,
      programmeCode: programmeCodeParam,
      dateFrom,
      dateTo,
    };
    return {
      key: AVIATION_STATS_QUERY_KEY,
      fetchFunction: getAviationStatsApi,
      params,
    };
  }, [code, dateFrom, dateTo, merchant?.code]);

  const { data: aviationStats, isLoading: isAviationStatsLoading } =  useReportData(dynamicAviationParams, isAviation);

  const popularCabinClassesTableConfig = useCallback((tableName: string) => ({
    tableHead: [
      { id: 'name', width: '20%', label: tableName, align: 'left' },
      { id: 'co2AmountKgs', width: '20%', label: 'Offset amount', align: 'right' },
      { id: 'numberOfPassengers', width: '20%', label: 'Passengers', align: 'right' },
      { id: 'numberOfTransactions', width: '20%', label: 'Transactions', align: 'right' },
      { id: 'percentageOfTransactions', width: '20%', label: 'Percentage', align: 'right' },
    ],
    tableRow: [
      { id: 'name', align: 'left', renderValue: (v: any) => readableStringFormatter(v?.cabinClass || v?.distanceCategory) },
      { id: 'co2AmountKgs', align: 'right', renderValue: (v: any) => `${formatNumber(v?.co2AmountKgs?.value)} ${v?.co2AmountKgs?.unit?.toLowerCase()}` },
      { id: 'numberOfPassengers', align: 'right', renderValue: (v: any) => v?.numberOfPassengers },
      { id: 'numberOfTransactions', align: 'right', renderValue: (v: any) => v?.numberOfTransactions },
      { id: 'percentageOfTransactions', align: 'right', renderValue: (v: any) => `${v?.percentageOfTransactions}%` },
    ],
  }), []);

  const programmeInvitationLink = merchant?.code && code && generateProgrammeLink(merchant?.code, code);
  
  const calculateStats = (data: ImpactRowReporting[]) => {
    const zeroStats = { co2Kgs: 0, numberOfContributions: 0 };

    return data ? data?.reduce(
      (acc, row) => {
        acc.co2Kgs += +row.metricValues[0].value;
        acc.numberOfContributions += +row.metricValues[1].value;
        return acc;
      },
      zeroStats
    ) : zeroStats;
  };

  const totalRangedImpactStats = useMemo(() => calculateStats(impactStats?.data?.rows), [impactStats?.data]);
  const totalMerchantImpact = useMemo(() => calculateStats(totalStats?.data?.rows), [totalStats?.data]);

  const listOfSDGs = useMemo(
    () => getListOfUniqSDGs(projectsRQ?.data.content),
    [projectsRQ?.data.content]
  );

  const projectsWithImpact = useMemo(() => {
    return projectsRQ?.data?.content?.map((project: Project) => ({
      impact: totalStats?.data?.projectImpacts?.find((impact: ProjectImpact) => impact?.projectId === project?.id),
      ...project,
    }))
  }, [projectsRQ?.data?.content, totalStats?.data?.projectImpacts]);

  const isAllDataLoaded = useMemo(() => {
    return !isLoadingTotalStats && !isLoadingImpactStats && !isLoadingProjects;
  }, [isLoadingTotalStats, isLoadingImpactStats, isLoadingProjects]);

  const summaryFlightDataset = useMemo(
    () => getSummaryDataByProgramme(aviationStats?.data, 'FLIGHT_DATA_SUMMARY'),
  [aviationStats?.data, getSummaryDataByProgramme]);

  const tableConfiguration = {
    tableHead: [
      { id: 'date', width: '25%', label: 'Period', align: 'left' },
      { id: 'co2_amount', width: '25%', label: 'Offset amount', align: 'right' },
      { id: 'end_of_period', width: '25%', label: 'End of period total', align: 'right' },
      { id: 'contributions', width: '25%', label: 'Contributions', align: 'right' },
    ],
    tableRow: [
      { id: 'date', align: 'left', renderValue: (v: TableRowProps) => v?.key },
      { id: 'co2_amount', align: 'right', renderValue: (v: TableRowProps) => `${formatNumber(v?.co2Kgs)} kg` },
      { id: 'end_of_period', align: 'right', renderValue: (v: TableRowProps) => `${formatNumber(v?.endOfPeriod)} kg` },
      { id: 'contributions', align: 'right', renderValue: (v: TableRowProps) => formatNumber(v?.numberOfContributions) },
    ],
  }

  const addTotalRow = (initialData: any) => {
    if (!initialData?.length) return [];
    const data = JSON.parse(JSON.stringify(initialData));

    let totalCO2 = 0;
    let totalPassengers = 0;
    let totalTransactions = 0;
    let totalPercentage = 0;

    data?.forEach((item: any) => {
      totalCO2 += item.co2AmountKgs.value;
      totalPassengers += item.numberOfPassengers;
      totalTransactions += item.numberOfTransactions;
      totalPercentage += item.percentageOfTransactions;
    });

    const totalRowName = initialData?.[0]?.distanceCategory ? { distanceCategory: 'Total' } : { cabinClass: 'Total' }
    const totalRow = {
      co2AmountKgs: { value: totalCO2, unit: 'KG' },
      numberOfPassengers: totalPassengers,
      numberOfTransactions: totalTransactions,
      percentageOfTransactions: totalPercentage,
      ...totalRowName,
    };

    if (data?.length) {
      return [...data, totalRow];
    }

    return data;
  }

  const popularCabinClassesTableData = addTotalRow(aviationStats?.data?.popularCabinClasses?.content);
  const popularDistanceCategoriesTableData = addTotalRow(aviationStats?.data?.popularDistanceCategories?.content);

  const barChartTableData = useMemo(() => {
    const data = impactStats?.data?.rows.map((row: ImpactRowReporting, index: number) => {
      const key = row.dimensionValues[0].value;
      const co2Kgs = parseFloat(row.metricValues[0].value);
      const numberOfContributions = parseInt(row.metricValues[1].value);
      const endOfPeriod = impactStats?.data.rows.slice(0, index + 1).reduce((acc: number, val: ImpactRowReporting) => acc + parseFloat(val.metricValues[0].value), 0);
      return { key, co2Kgs, numberOfContributions, endOfPeriod };
    })

    if (data?.length) {
      data.push({
        key: 'Total',
        co2Kgs: data.reduce((a: number, b: ReportingTooltip) => a + b.co2Kgs, 0),
        numberOfContributions: data.reduce((a: number, b: ReportingTooltip) => a + b.numberOfContributions, 0),
        endOfPeriod: data.reduce((a: number, b: ReportingTooltip) => a + b.co2Kgs, 0),
      })
    }

    return data;
  }, [impactStats?.data?.rows]);

  const lineChartTableData = useMemo(() => {
    const data = totalStats?.data?.rows.map((row: ImpactRowReporting, index: number) => {
      const key = row.dimensionValues[0].value;
      const co2Kgs = parseFloat(row.metricValues[0].value);
      const numberOfContributions = totalStats?.data?.rows.slice(0, index + 1).reduce((acc: number, val: ImpactRowReporting) => acc + parseFloat(val.metricValues[1].value), 0);
      const endOfPeriod = totalStats?.data?.rows.slice(0, index + 1).reduce((acc: number, val: ImpactRowReporting) => acc + parseFloat(val.metricValues[0].value), 0);
      return { key, co2Kgs, endOfPeriod, numberOfContributions };
    })

    const res = data?.map((el: TableRowProps, index: number) => {
      return {
        ...el,
        endOfPeriod: data?.slice(0, index + 1).reduce((acc: number, val: any) => acc + parseFloat(val.co2Kgs), 0)
      }
    })

    if (res?.length) {
      res.push({
        key: 'Total',
        co2Kgs: data.reduce((a: number, b: TableRowProps) => a + b.co2Kgs, 0),
        numberOfContributions: res[res?.length - 1].numberOfContributions,
        endOfPeriod: data.reduce((a: number, b: TableRowProps) => a + b.co2Kgs, 0),
      })
    }

    return res;
  }, [totalStats?.data?.rows]);

  if (!isAllDataLoaded) {
    return <LoadingScreen />
  }

  return (
    <div>
      <Container maxWidth={'md'} className="container" sx={{ px: '8px !important' }}>
        <Instruction />

        <div className="page">
          <Stack sx={{ mt: 10, mb: 16 }} direction={'row'} justifyContent={'flex-end'}>
            <Logo sx={{ '& img': { height: 46 } }} />
          </Stack>

          <Stack spacing={2} sx={{ my: 5 }}>
            <Typography variant="h1" sx={{ fontFamily: FONT_FAMILY }}>
              Impact report
            </Typography>
            <Typography
              variant="h2"
              sx={{ color: (theme) => theme.palette.grey[500], fontFamily: FONT_FAMILY }}
            >
              {merchant?.name}
            </Typography>
            <Typography variant="h5" color={'primary'}>
              {dateFrom && format(new Date(dateFrom), 'MMMM d, yyyy')} -{' '}
              {dateTo && format(new Date(dateTo), 'MMMM d, yyyy')}
            </Typography>
          </Stack>

          <Stack
            gap={{ xs: 3, sm: 6 }}
            direction={'row'}
            justifyContent={'center'}
            sx={{ my: 14, width: '100%' }}
            flexWrap={'wrap'}
          >
            <GenericImpactMetric
              loading={false}
              items={[
                {
                  data: { value: totalRangedImpactStats?.numberOfContributions },
                  title: 'Total contributions',
                  className: 'primary',
                  key: 'CONTRIBUTIONS',
                },
                {
                  data: { value: totalRangedImpactStats?.co2Kgs, unit: 'kg' },
                  title: 'Amount of carbon mitigiated',
                  className: 'secondary',
                  key: 'CARBON',
                },
              ]}
            />
            <SdgCountTile sdgCount={listOfSDGs?.length} />
          </Stack>
        </div>

        <div className="pageBreak" />

        <div className="page">
          <Typography variant="h3" gutterBottom sx={{ fontFamily: FONT_FAMILY }}>
            Insights
          </Typography>
          <Typography variant="h5" color={'primary'}>
            {dateFrom && format(new Date(dateFrom), 'MMMM d, yyyy')} -{' '}
            {dateTo && format(new Date(dateTo), 'MMMM d, yyyy')}
          </Typography>
          <Box sx={{ pb: 5 }} />

          <Stack spacing={2} sx={{ '& .tileWrapper': { boxShadow: 'none', p: 0 } }}>
            <Title text="Impact over time" />
            <Typography variant='body2'>
              The graphs below show a breakdown of carbon offsets over time and the corresponding
              accumulation for the selected reporting period covering {dateFrom && format(new Date(dateFrom), 'MMMM d, yyyy')}, to {dateTo && format(new Date(dateTo), 'MMMM d, yyyy')}.
            </Typography>
            <Stack gap={2} direction={{ xs: 'column', sm: 'row' }} sx={{ '& .MuiGrid-item': { width: { xs: '100%', sm: '50%' } } }}>
              <ImpactGraphs
                showBarChart
                dataset={impactStats?.data}
                previousImpact={{ co2Kgs: 0, numberOfContributions: 0 }}
              />
            </Stack>
            <SimpleTable tableData={barChartTableData} tableConfig={tableConfiguration} />
          </Stack>
        </div>

        {isAviation && (
          <div>
            <div className='page'>
              <div className="pageBreak" />

              <Stack spacing={2} sx={{ mb: 2 }}>
                <Summary
                  isLoading={isAviationStatsLoading}
                  dataset={summaryFlightDataset}
                  link={programmeInvitationLink}
                />

                <CabinClassBreakdown
                  isLoading={isAviationStatsLoading}
                  data={aviationStats?.data?.popularCabinClasses?.content}
                  sx={{ maxWidth: 500, mx: 'auto !important' }}
                />
                <SimpleTable tableData={popularCabinClassesTableData || []} tableConfig={popularCabinClassesTableConfig('Class')} />
              </Stack>
            </div>

            <div className='page'>
              <div className="pageBreak" />

              <Stack spacing={2}>
                <DistanceBreakdown
                  isLoading={isAviationStatsLoading}
                  data={aviationStats?.data?.popularDistanceCategories?.content}
                  sx={{ maxWidth: 400, mx: 'auto !important' }}
                />
                <SimpleTable tableData={popularDistanceCategoriesTableData || []} tableConfig={popularCabinClassesTableConfig('Category')} />
              </Stack>
            </div>
          </div>
        )}

        <div className="pageBreak" />

        <div className="page">
          <Stack spacing={2} sx={{ '& .MuiGrid-root': { border: 'none' } }}>
            <Title text="Supported projects" />
            <Typography variant='body2' sx={{ mb: 2 }}>
              Below is a list of all projects that the programme has supported in the report date range, including those that are no longer part of your current project portfolio.
            </Typography>

            <Projects projects={projectsWithImpact} />
          </Stack>
        </div>

        <div className="pageBreak" />

        <div className="page">
          <Stack spacing={2}>
            <Title text="Sustainable development goals" />

            <Typography variant='body2'>
              The United Nations Sustainable Development Goals (SDGs) are a set of 17 global
              objectives aimed at addressing pressing challenges such as poverty, inequality,
              climate change, and more. They provide a universal roadmap for a sustainable,
              equitable, and prosperous future.
            </Typography>
            <Typography variant='body2'>
              The projects you’ve been supporting are contributing to{' '}
              <b>{listOfSDGs?.length} Sustainable Development Goals</b> combined, making a positive
              impact on the planet and local communities.
            </Typography>
            <Grid container columns={{ xs: 1, sm: 2 }}>
              {listOfSDGs?.map((sdg, id) => 
                sdg && (
                  <Stack
                    sx={{ my: 0.5, width: { xs: '100%', sm: '48%' }, mr: { sm: id % 2 === 0 ? 3 : 0 } }}
                    spacing={2}
                    direction={'row'}
                    alignItems={'center'}
                    key={id}
                  >
                    <Box>
                      <Image sx={{ width: 60 }} src={sdg?.imageUrl} visibleByDefault alt="project goal" />
                    </Box>
                    <Stack>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {sdg?.title}
                      </Typography>
                      <Typography sx={{ lineHeight: 1.3, fontSize: { xs: 12, md: 14 } }}>{sdg?.description}</Typography>
                    </Stack>
                  </Stack>
                )
              )}
            </Grid>
          </Stack>
        </div>

        <div className="pageBreak" />

        <div className="page">
          <Typography variant="h3" gutterBottom sx={{ fontFamily: FONT_FAMILY }}>
            Summary
          </Typography>
          <Typography variant="h6" color={'primary'}>All time programme impact</Typography>
          <Box sx={{ pb: 4 }} />
          <TotalImpactSection totalMerchantImpact={totalMerchantImpact} listOfSDGs={listOfSDGs} />
          <Box sx={{ pb: 6 }} />
          <CumulativeImpactSection
              dataset={totalStats?.data}
              previousImpact={{ co2Kgs: 0, numberOfContributions: 0 }}
              tableData={lineChartTableData}
              tableConfig={tableConfiguration}
          />
        </div>
      </Container>
    </div>
  );
};

export default MerchantReport;
